import { useQuery } from '@tanstack/react-query';
import { LoaderCircle, RotateCw, VideoOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '../../components/Button';
import { StatusBadge } from '../../components/StatusBadge';
import { ApiError } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import {
  describeLiveStats,
  getLiveStats,
  isTransientLiveFailure,
  liveErrorMessage,
  liveStatsKey,
  startHLS,
  startWebRTC,
  type LocalFailure,
} from './api';
import {
  forgetWebRTCFailure,
  rememberWebRTCFailure,
  webrtcRecentlyFailed,
} from './transportMemory';

/** How long WebRTC media may take to start after signalling succeeded. */
const WEBRTC_START_MS = 15_000;
/** HLS starts slower: the engine muxes on demand and the player buffers segments. */
const HLS_START_MS = 30_000;
/** A playing video whose time stands still this long is treated as dropped. */
const STALL_MS = 20_000;
/** Pause before the first silent reconnection of a video that was playing. */
const RECONNECT_MS = 1_000;

/** Automatic retries back off from 2 s to 30 s while the failure lasts. */
const retryDelayMs = (retries: number) => Math.min(2_000 * 2 ** retries, 30_000);

/**
 * Where the video comes from when it is not the device's live stream (e.g.
 * recordings kept on the device). Changing `key` starts a new session.
 */
export interface VideoSource {
  key: string;
  /** Badge while playing, instead of "Ao vivo" (e.g. "Gravação"). */
  badge?: string;
  webrtc: (sdp: string, signal: AbortSignal) => Promise<{ sdp: string }>;
  hls: (signal: AbortSignal) => Promise<{ url: string }>;
}

interface Props {
  deviceId: string;
  profileToken: string;
  /** Overrides the live stream; the device id is then only a label. */
  source?: VideoSource;
  /** Seconds of video played since the session started. */
  onProgress?: (seconds: number) => void;
  /** Accessible name of the video, e.g. the camera name. */
  label: string;
  /** "tile" fills a mosaic cell and uses compact messages. */
  variant?: 'single' | 'tile';
}

type Transport = 'webrtc' | 'hls';

const webrtcSupported = () => typeof RTCPeerConnection !== 'undefined';

/**
 * Live view of one device profile: WebRTC first; when the media path cannot
 * be established (UDP blocked, strict NAT) it falls back to HLS relayed by the
 * API. Each (device, profile, attempt) is a fresh session; unmounting always
 * releases it so the engine can close the camera ingest when nobody watches.
 * Live views recover by themselves from transient failures (camera or link
 * down for a while), backing off between attempts.
 */
export function LivePlayer({
  deviceId,
  profileToken,
  source,
  onProgress,
  label,
  variant = 'single',
}: Props) {
  // retries: automatic attempts in a row since the video last played.
  const [run, setRun] = useState({ attempt: 0, retries: 0 });
  return (
    <Session
      key={`${deviceId}|${profileToken}|${source?.key ?? ''}|${String(run.attempt)}`}
      deviceId={deviceId}
      profileToken={profileToken}
      {...(source ? { source } : {})}
      {...(onProgress ? { onProgress } : {})}
      label={label}
      variant={variant}
      retries={run.retries}
      onRetry={(auto) => {
        // The viewer asking again tries WebRTC again; automatic attempts keep
        // what the tab learned about the network.
        if (!auto) forgetWebRTCFailure();
        setRun((r) => ({ attempt: r.attempt + 1, retries: auto ? r.retries + 1 : 0 }));
      }}
      onPlaying={() => {
        setRun((r) => (r.retries ? { ...r, retries: 0 } : r));
      }}
    />
  );
}

type Phase =
  | { name: 'connecting' }
  | { name: 'playing' }
  | { name: 'reconnecting' }
  // retryAt: when the next automatic attempt starts, if there is one.
  | { name: 'error'; message: string; retryAt?: number };

function Session({
  deviceId,
  profileToken,
  source,
  onProgress,
  label,
  variant,
  retries,
  onRetry,
  onPlaying: onSessionPlaying,
}: Props & { retries: number; onRetry: (auto: boolean) => void; onPlaying: () => void }) {
  // Latest callbacks without restarting the session when they change.
  const progress = useRef(onProgress);
  const parent = useRef({ retries, onRetry, onSessionPlaying });
  useEffect(() => {
    progress.current = onProgress;
    parent.current = { retries, onRetry, onSessionPlaying };
  });
  const tile = variant === 'tile';
  const { request, accessToken } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [transport, setTransport] = useState<Transport>(() =>
    webrtcSupported() && !webrtcRecentlyFailed() ? 'webrtc' : 'hls',
  );
  const [phase, setPhase] = useState<Phase>({ name: 'connecting' });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const abort = new AbortController();
    let timer: number | undefined;
    let retryTimer: number | undefined;
    let release: () => void = () => undefined;
    // Whether this session showed video: a later failure is a drop, not a
    // network that cannot carry WebRTC.
    let played = false;
    let lastTime = -1;
    let lastProgressAt = 0;

    const fail = (err: unknown) => {
      if (abort.signal.aborted) return;
      window.clearTimeout(timer);
      window.clearInterval(watchdog);
      release();
      // Media-path failures on WebRTC get a second chance over HLS, and so
      // does an engine that refuses the WebRTC session (503: e.g. a codec the
      // browser cannot take over WebRTC). An unreachable camera (502) is not
      // retried: HLS would fail the same way.
      const engineRefused = err instanceof ApiError && err.status === 503;
      const mediaPath = !played && (err === 'ice_failed' || err === 'timeout');
      if (transport === 'webrtc' && !played && (mediaPath || engineRefused)) {
        // The network, not this camera: the next views skip WebRTC for a while.
        if (mediaPath) rememberWebRTCFailure();
        setTransport('hls');
        return;
      }
      // Recordings restart from their beginning: the viewer decides.
      if (source || !isTransientLiveFailure(err)) {
        setPhase({ name: 'error', message: liveErrorMessage(err) });
        return;
      }
      // A video that was playing reconnects quietly first; one that cannot
      // start says why while it keeps trying.
      const delay = played ? RECONNECT_MS : retryDelayMs(parent.current.retries);
      setPhase(
        played
          ? { name: 'reconnecting' }
          : { name: 'error', message: liveErrorMessage(err), retryAt: Date.now() + delay },
      );
      retryTimer = window.setTimeout(() => {
        parent.current.onRetry(true);
      }, delay);
    };
    const onPlaying = () => {
      window.clearTimeout(timer);
      played = true;
      lastProgressAt = Date.now();
      setPhase((p) => (p.name === 'connecting' ? { name: 'playing' } : p));
      parent.current.onSessionPlaying();
    };
    video.addEventListener('playing', onPlaying);
    const onTime = () => {
      progress.current?.(video.currentTime);
    };
    video.addEventListener('timeupdate', onTime);
    // Neither WebRTC nor hls.js always reports a source that stops sending
    // (the connection stays up, the playlist stops growing): watch the clock.
    const watchdog = window.setInterval(() => {
      // Browsers may pause muted video in background tabs; that is no drop.
      if (!played || document.hidden || video.currentTime !== lastTime) {
        lastTime = video.currentTime;
        lastProgressAt = Date.now();
        return;
      }
      if (Date.now() - lastProgressAt >= STALL_MS) fail('stalled' satisfies LocalFailure);
    }, 2_000);
    const startTimer = (ms: number) => {
      timer = window.setTimeout(() => {
        fail('timeout' satisfies LocalFailure);
      }, ms);
    };

    if (transport === 'webrtc') {
      const pc = new RTCPeerConnection();
      release = () => {
        pc.close();
      };
      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });
      pc.ontrack = (e) => {
        const stream = e.streams[0] ?? new MediaStream([e.track]);
        if (video.srcObject !== stream) video.srcObject = stream;
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed') fail('ice_failed' satisfies LocalFailure);
      };
      void (async () => {
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          const answer = source
            ? await source.webrtc(offer.sdp ?? '', abort.signal)
            : await startWebRTC(request, deviceId, offer.sdp ?? '', profileToken, abort.signal);
          if (abort.signal.aborted) return;
          await pc.setRemoteDescription({ type: 'answer', sdp: answer.sdp });
          startTimer(WEBRTC_START_MS);
        } catch (err) {
          fail(err);
        }
      })();
    } else {
      void (async () => {
        try {
          const [{ default: Hls }, st] = await Promise.all([
            import('hls.js/light'),
            source
              ? source.hls(abort.signal)
              : startHLS(request, deviceId, profileToken, abort.signal),
          ]);
          if (abort.signal.aborted) return;
          if (!Hls.isSupported()) {
            fail('hls_unsupported' satisfies LocalFailure);
            return;
          }
          const hls = new Hls({
            // Playlists and segments come from the VMS API, authorised per request.
            xhrSetup: (xhr) => {
              const token = accessToken();
              if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            },
            liveSyncDurationCount: 2,
            manifestLoadingTimeOut: 20_000,
          });
          release = () => {
            hls.destroy();
          };
          hls.on(Hls.Events.ERROR, (_event, data) => {
            if (data.fatal) fail('hls_failed' satisfies LocalFailure);
          });
          hls.loadSource(st.url);
          hls.attachMedia(video);
          startTimer(HLS_START_MS);
        } catch (err) {
          fail(err);
        }
      })();
    }

    return () => {
      abort.abort();
      window.clearTimeout(timer);
      window.clearTimeout(retryTimer);
      window.clearInterval(watchdog);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('timeupdate', onTime);
      release();
      video.srcObject = null;
    };
    // The session is keyed by the source (see LivePlayer), not by its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, accessToken, deviceId, profileToken, transport]);

  return (
    <div
      className={`relative overflow-hidden bg-black ${
        tile ? 'size-full' : 'aspect-video w-full rounded-md border border-line'
      }`}
    >
      <video
        ref={videoRef}
        aria-label={`Vídeo ao vivo: ${label}`}
        className="size-full object-contain"
        autoPlay
        muted
        playsInline
      />
      {/* In a tile the error panel says it all; the badge would sit under it. */}
      <div
        hidden={tile && phase.name === 'error'}
        className="absolute top-2 left-2 rounded-full bg-canvas/85 shadow-sm"
        title={
          transport === 'hls'
            ? 'WebRTC indisponível nesta rede: reproduzindo por HLS, com alguns segundos de atraso.'
            : undefined
        }
      >
        {phase.name === 'playing' ? (
          // A recording is amber like the replay mode; live is green unless degraded to HLS.
          <StatusBadge tone={source?.badge || transport === 'hls' ? 'warn' : 'ok'} pulse>
            {`${source?.badge ?? 'Ao vivo'}${transport === 'hls' ? ' · HLS' : ''}`}
          </StatusBadge>
        ) : phase.name === 'reconnecting' || (phase.name === 'connecting' && retries > 0) ? (
          <StatusBadge tone="warn">Reconectando</StatusBadge>
        ) : phase.name === 'connecting' ? (
          <StatusBadge tone="neutral">
            {transport === 'hls' ? 'Conectando (HLS)' : 'Conectando'}
          </StatusBadge>
        ) : (
          <StatusBadge tone="crit">Sem vídeo</StatusBadge>
        )}
      </div>
      {!source && phase.name === 'playing' && (
        <StreamStats deviceId={deviceId} profileToken={profileToken} tile={tile} />
      )}
      {(phase.name === 'connecting' || phase.name === 'reconnecting') && (
        <div className="absolute inset-0 grid place-items-center" role="status">
          <LoaderCircle aria-hidden="true" className="size-6 animate-spin text-fg-muted" />
          <span className="sr-only">
            {phase.name === 'reconnecting' || retries > 0
              ? 'Reconectando ao vídeo'
              : 'Conectando ao vídeo'}
          </span>
        </div>
      )}
      {phase.name === 'error' && (
        <div
          role="alert"
          className={`absolute inset-0 flex flex-col items-center justify-center bg-canvas/90 text-center ${
            // Tiles keep clear of the caption bar the mosaic draws at the bottom.
            tile ? 'gap-1.5 px-3 pt-2 pb-8' : 'gap-3 px-6'
          }`}
        >
          <VideoOff
            aria-hidden="true"
            className={`${tile ? 'size-4' : 'size-6'} text-crit`}
            strokeWidth={1.75}
          />
          <p
            title={tile ? phase.message : undefined}
            className={
              tile
                ? 'line-clamp-2 max-w-xs text-2xs leading-snug text-fg'
                : 'max-w-sm text-[13px] leading-relaxed text-fg'
            }
          >
            {phase.message}
          </p>
          {phase.retryAt !== undefined && <RetryCountdown at={phase.retryAt} tile={tile} />}
          <Button
            onClick={() => {
              onRetry(false);
            }}
            className={tile ? 'h-6! shrink-0 px-2! text-2xs!' : ''}
          >
            <RotateCw aria-hidden="true" className="size-3.5" />
            Tentar novamente
          </Button>
        </div>
      )}
    </div>
  );
}
/** Seconds left before the player tries again by itself. */
function RetryCountdown({ at, tile }: { at: number; tile: boolean }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, 1_000);
    return () => {
      window.clearInterval(id);
    };
  }, []);
  const seconds = Math.max(1, Math.ceil((at - now) / 1_000));
  return (
    <p className={`text-fg-muted tabular-nums ${tile ? 'text-2xs' : 'text-xs'}`}>
      Nova tentativa automática em {seconds} s
    </p>
  );
}

/**
 * What comes from the device (codec, resolution, frame rate, rate), as the
 * media server measures it, refreshed while the video plays.
 */
function StreamStats({
  deviceId,
  profileToken,
  tile,
}: {
  deviceId: string;
  profileToken: string;
  tile: boolean;
}) {
  const { request } = useAuth();
  const stats = useQuery({
    queryKey: liveStatsKey(deviceId, profileToken),
    queryFn: () => getLiveStats(request, deviceId, profileToken),
    refetchInterval: 5_000,
    retry: false,
  });
  if (!stats.data?.active) return null;
  const text = describeLiveStats(stats.data);
  return (
    <div
      title={`Recebido do equipamento: ${text}`}
      className={`absolute top-2 right-2 max-w-[calc(100%-6rem)] truncate rounded bg-canvas/85 px-1.5 py-0.5 font-mono text-fg-muted tabular-nums shadow-sm ${
        tile ? 'text-[10px]' : 'text-2xs'
      }`}
    >
      <span className="sr-only">Recebido do equipamento: </span>
      {text}
    </div>
  );
}
